const API_URL = process.env.REACT_APP_API_URL;

const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['Authentication'] = `Bearer ${token}`;
  }
  return headers;
};

const extractBackendErrorMessage = async (
  response: Response,
  fallbackMessage: string
): Promise<string> => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data && typeof data === 'object' && typeof (data as any).message === 'string') {
        return (data as any).message;
      }
    } else {
      const text = await response.text();
      if (text && text.trim()) return text;
    }
  } catch {
    // fallback
  }
  return fallbackMessage;
};

export interface CloseBatchRequest {
  transactionIds: string[];
  dataVencimento: string; // yyyy-MM-dd
}

export interface AutoBoletoResponse {
  id: string;
  codigoIntegracao: string;
  /** Número humano da fatura (FAT-<ano>-<seq>). Nulo em registros anteriores à migration V38. */
  numeroFatura: string | null;
  status: string;
  payerType: string | null;
  payerBillingInfoId: string | null;
  valorTotal: number;
  dataEmissao: string | null;
  dataVencimento: string | null;
  dataPagamento: string | null;
  numeroBoleto: string | null;
  linhaDigitavel: string | null;
  codigoBarras: string | null;
  codigoLancamentoOmie: number | null;
  lastError: string | null;
  attempts: number | null;
  qtdeVeiculos: number | null;
  transactionIds: string[] | null;
  createdAt: string;
}

export interface BoletoListFilters {
  status?: string;
  /** Busca parcial e case-insensitive no número da fatura (FAT-<ano>-<seq>) ou no nº do boleto (OMIE). */
  busca?: string;
  start?: string;
  end?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface BoletoPageResponse {
  content: AutoBoletoResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export const billingService = {
  async closeBatch(request: CloseBatchRequest): Promise<AutoBoletoResponse> {
    const url = `${API_URL}/api/v1/contracts/billing/close-batch`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível emitir o boleto. Verifique os dados e tente novamente.');
      throw new Error(message);
    }
    return response.json();
  },

  async listBoletos(filters: BoletoListFilters = {}): Promise<BoletoPageResponse> {
    const query = new URLSearchParams();
    if (filters.status?.trim()) query.set('status', filters.status.trim());
    if (filters.busca?.trim()) query.set('q', filters.busca.trim());
    if (filters.start?.trim()) query.set('start', filters.start.trim());
    if (filters.end?.trim()) query.set('end', filters.end.trim());
    query.set('page', String(filters.page ?? 0));
    query.set('size', String(filters.size ?? 10));
    query.set('sort', filters.sort?.trim() || 'createdAt,desc');

    const url = `${API_URL}/api/v1/contracts/billing/boletos?${query.toString()}`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível carregar a lista de boletos. Por favor, tente novamente.');
      throw new Error(message);
    }

    const data = await response.json();
    return {
      content: Array.isArray(data?.content) ? data.content : [],
      totalElements: Number(data?.totalElements ?? 0),
      totalPages: Number(data?.totalPages ?? 0),
      size: Number(data?.size ?? (filters.size ?? 10)),
      number: Number(data?.number ?? (filters.page ?? 0)),
      first: Boolean(data?.first),
      last: Boolean(data?.last)
    };
  },

  async cancelBoleto(boletoId: string): Promise<AutoBoletoResponse> {
    const url = `${API_URL}/api/v1/contracts/billing/boletos/${boletoId}`;
    const response = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível cancelar o boleto. Por favor, tente novamente.');
      throw new Error(message);
    }
    return response.json();
  },

  async reemitirBoleto(boletoId: string): Promise<AutoBoletoResponse> {
    const url = `${API_URL}/api/v1/contracts/billing/boletos/${boletoId}/reemitir`;
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders() });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível reemitir o boleto. Por favor, tente novamente.');
      throw new Error(message);
    }
    return response.json();
  },

  async syncBoleto(boletoId: string): Promise<AutoBoletoResponse> {
    const url = `${API_URL}/api/v1/contracts/billing/boletos/${boletoId}/sync`;
    const response = await fetch(url, { method: 'POST', headers: getAuthHeaders() });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível sincronizar o boleto. Por favor, tente novamente.');
      throw new Error(message);
    }
    return response.json();
  },

  async downloadBoletoPdf(boletoId: string): Promise<void> {
    const url = `${API_URL}/api/v1/contracts/billing/boletos/${boletoId}/pdf`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) {
      const message = await extractBackendErrorMessage(response, 'Não foi possível baixar o PDF do boleto. Por favor, tente novamente.');
      throw new Error(message);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // libera após dar tempo de o navegador abrir
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  }
};
